import { TextFieldProps, TextField } from '@mui/material';
import { Field, FieldProps } from 'formik';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';

interface CommonTextFieldProps {
  name: string;
  readOnly?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  smallFontSize?: boolean;
  noLabel?: boolean;
  smallPadding?: boolean;
  maxTextLength?: number;
  variant?: 'outlined' | 'standard' | 'filled';
  labelText?: string;
}

const useStyles = makeStyles(() => ({
  input: {
    '& .MuiOutlinedInput-input': {
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
      },
    },
  },
  smallFontSize: {
    '& input': {
      fontSize: 12,
    },
    '&.MuiOutlinedInput-multiline': {
      fontSize: 12,
    },
  },
  smallPadding: {
    '& input': {
      padding: 4,
    },
    '&.MuiOutlinedInput-multiline': {
      padding: 4,
    },
  },
}));

export function CommonTextField(props: CommonTextFieldProps & TextFieldProps) {
  const { t } = useTranslation();
  const {
    name,
    readOnly = false,
    fullWidth = false,
    helperText = undefined,
    InputProps = {},
    size = 'small',
    smallFontSize = false,
    noLabel = false,
    smallPadding = false,
    maxTextLength = 0,
    variant = 'outlined',
    labelText = '',
    label: labelProp,
    ...rest
  } = props;
  const classes = useStyles();

  const nameSplit = name.split('.');
  const nameKey =
    labelText === '' ? nameSplit[nameSplit.length - 1] : labelText;
  // If the caller passes an explicit `label` prop, use it directly for both
  // label and placeholder. Otherwise fall back to the i18n-translated field name.
  const label = labelProp ?? (noLabel ? undefined : t(`attributes.${nameKey}`));

  return (
    <Field name={name}>
      {({ field, meta }: FieldProps) => (
        <TextField
          {...rest}
          {...field}
          id={name}
          size={size}
          variant={variant}
          fullWidth={fullWidth}
          label={label ?? undefined}
          placeholder={typeof label === 'string' ? label : undefined}
          inputProps={{
            maxLength: maxTextLength > 0 ? maxTextLength : undefined,
            style: {
              color: variant === 'standard' ? 'Highlight' : undefined,
            },
          }}
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
          error={meta.touched && Boolean(meta.error)}
          helperText={helperText || (meta.touched && meta.error)}
          InputProps={{
            className: clsx([
              classes.input,
              {
                [classes.smallFontSize]: smallFontSize,
                [classes.smallPadding]: smallPadding,
              },
            ]),
            readOnly,
            ...InputProps,
          }}
          InputLabelProps={{
            shrink: true,
            style: {
              color: variant === 'standard' ? 'Highlight' : undefined,
            },
          }}
        />
      )}
    </Field>
  );
}
